import { useState } from 'react';

export function AnalysisPage() {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        setFile(e.target.files?.[0] ?? null);
    }

    async function handleUpload() {
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            await fetch('/api/sequences/upload', { method: 'POST', body: formData });
            setFile(null);
        } catch {
            // handle error
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="animate-in">
            <header className="page-header">
                <h1 className="page-title">Genomic Analysis</h1>
                <p className="page-subtitle">Upload sequences and run AI-powered analysis</p>
            </header>

            <div className="grid grid-2">
                <div className="card">
                    <h2 className="card-title" style={{ marginBottom: 16 }}>Upload Sequence</h2>
                    <UploadZone file={file} onChange={handleFileChange} />
                    <button
                        className="btn btn-primary"
                        style={{ marginTop: 16, width: '100%' }}
                        onClick={handleUpload}
                        disabled={!file || uploading}
                    >
                        {uploading ? 'Uploading...' : 'Upload & Analyze'}
                    </button>
                </div>

                <div className="card">
                    <h2 className="card-title" style={{ marginBottom: 16 }}>Analysis Types</h2>
                    <AnalysisOption
                        title="Variant Calling"
                        description="DeepVariant-powered mutation detection"
                        icon="🧬"
                    />
                    <AnalysisOption
                        title="Protein Structure"
                        description="AlphaFold 3D structure prediction"
                        icon="🔬"
                    />
                    <AnalysisOption
                        title="Genome Interpretation"
                        description="AlphaGenome non-coding DNA analysis"
                        icon="📊"
                    />
                </div>
            </div>

            <div className="card" style={{ marginTop: 24 }}>
                <h2 className="card-title" style={{ marginBottom: 16 }}>Recent Jobs</h2>
                <JobsTable />
            </div>
        </div>
    );
}

function UploadZone({ file, onChange }: { file: File | null; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) {
    return (
        <label
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 40,
                border: '2px dashed var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                cursor: 'pointer',
                transition: 'border-color var(--transition)',
            }}
        >
            <input type="file" accept=".fasta,.fastq,.fa,.fq" onChange={onChange} style={{ display: 'none' }} />
            <span style={{ fontSize: 40, marginBottom: 12 }}>📁</span>
            <span>{file ? file.name : 'Drop FASTA/FASTQ file or click to browse'}</span>
        </label>
    );
}

function AnalysisOption({ title, description, icon }: { title: string; description: string; icon: string }) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: 16,
                borderRadius: 'var(--radius-md)',
                marginBottom: 8,
                background: 'var(--color-bg)',
                cursor: 'pointer',
            }}
        >
            <span style={{ fontSize: 28 }}>{icon}</span>
            <div>
                <div style={{ fontWeight: 600 }}>{title}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-dim)' }}>{description}</div>
            </div>
        </div>
    );
}

function JobsTable() {
    return (
        <div style={{ color: 'var(--color-text-dim)' }}>
            No analysis jobs yet. Upload a sequence to get started.
        </div>
    );
}
